import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { provideState, injectState } from 'freactal'
import styled from 'styled-components'
import { Route } from 'react-router-dom'
import app from '../../../../feathers'

import Button from '../../../../components/Button'
import FullAreaContent from '../../../components/FullAreaContent'
import Accordion from '../../../components/Accordion'
import SectionNameField from '../../../components/SectionNameField'
import SectionPanel from './SectionPanel'
import MappingFooter from './MappingFooter'
import SectionMapping from './SectionMapping'
import QuestionMapping from './QuestionMapping'
import QuestionContent from './QuestionContent'
import ConfirmDelete from '../../../../components/ConfirmDelete'

import {
  getSection,
  sectionDisplayIndex,
  getSectionIndexArray,
} from '../../../../lib'

const StyledAccordion = styled(Accordion)`
  height: calc(70% - 50px);
  overflow-y: auto;
  padding: 1em;
`
const ActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 50px;
  padding: 0 0.5em;
`
const SectionHeader = styled.div`
  border: 1px solid black;
  background-color: ${({ theme }) => theme.main};
  .mdl-textfield {
    padding: 4px 0;
  }
  input {
    font-size: 1.5em;
  }
`
const SectionID = styled.span`
  display: inline-block;
  width: 4em;
  padding-left: 0.5em;
  font-size: 1.5em;
  font-weight: bold;
`

const withState = provideState({
  initialState: () => ({
    sectionAddError: null,
    sectionDeleteError: null,
  }),
  effects: {
    addSection: async (effects, { _id, parentsectionIndex }) => {
      try {
        const path = parentsectionIndex
          ? `sections.${parentsectionIndex
              .split('.')
              .join('.subsections.')}.subsections`
          : 'sections'
        await app.service('units').patch(_id, {
          $push: {
            [path]: { name: 'New Section' },
          },
        })
      } catch (sectionAddError) {
        return state => ({ ...state, sectionAddError })
      }
    },
    deleteSection: async (effects, { _id, parentsectionIndex, sectionId }) => {
      try {
        const path = parentsectionIndex
          ? `sections.${parentsectionIndex
              .split('.')
              .join('.subsections.')}.subsections`
          : 'sections'
        await app.service('units').patch(_id, {
          $pull: {
            [path]: { _id: sectionId },
          },
        })
      } catch (sectionDeleteError) {
        return state => ({ ...state, sectionDeleteError })
      }
    },
  },
})

class Content extends Component {
  static contextTypes = {
    prompt: PropTypes.func,
  }
  handleDeleteSection = sectionIndex => async () => {
    const { state: { unit }, effects: { deleteSection } } = this.props
    const sectionIndexArr = getSectionIndexArray(sectionIndex)
    const section = getSection(unit, sectionIndexArr)
    const response = await this.context.prompt({
      title: `Delete ${sectionDisplayIndex(...sectionIndexArr)}`,
      ContentComponent: ConfirmDelete,
    })
    if (response === 'Delete') {
      deleteSection({
        _id: unit._id,
        parentsectionIndex: sectionIndexArr
          .slice(0, sectionIndexArr.length - 1)
          .join('.'),
        sectionId: section._id,
      })
    }
  }
  handleAddSection = async () => {
    const { state: { unit }, effects: { addSection } } = this.props
    const response = await this.context.prompt({
      title: 'New Section',
      message: 'Choose the section level you would like to add.',
      options: ['X.X.X Level', 'X.X.X.X Level', 'Cancel'],
    })
    if (response === 'Cancel') {
      return
    }
    let { match: { params: { sectionIndex } } } = this.props
    const section = this.getCurrentSection()
    const parentsectionIndex = (response === 'X.X.X Level'
      ? getSectionIndexArray(sectionIndex)
      : [
          ...getSectionIndexArray(sectionIndex),
          section.subsections.length - 1,
        ]).join('.')
    addSection({
      _id: unit._id,
      parentsectionIndex,
    })
  }
  getCurrentSection() {
    const { state: { unit } } = this.props
    let { match: { params: { sectionIndex } } } = this.props
    sectionIndex = sectionIndex.split('.').map(str => parseInt(str, 10))
    const section = unit.sections[sectionIndex[0]].subsections[sectionIndex[1]]
    return section
  }
  render() {
    const { unit } = this.props.state
    let { match: { params: { sectionIndex } } } = this.props
    sectionIndex = sectionIndex.split('.').map(str => parseInt(str, 10))
    const section = this.getCurrentSection()
    const sections = section.subsections.reduce(
      (sections, subsection, i) => {
        sections.push(
          { ...subsection, sectionIndex: [...sectionIndex, i] },
          ...subsection.subsections.map((section, j) => ({
            ...section,
            sectionIndex: [...sectionIndex, i, j],
          })),
        )
        return sections
      },
      [{ ...section, sectionIndex }],
    )
    return (
      <FullAreaContent>
        <StyledAccordion>
          {sections.map(
            (
              { _id, name, content, sectionIndex, questions, subsections },
              i,
            ) => (
              <SectionPanel
                header={
                  <SectionHeader>
                    <SectionID>
                      {sectionDisplayIndex(...sectionIndex)}
                    </SectionID>
                    <SectionNameField
                      style={{ width: '80%', padding: 0 }}
                      name={name}
                      unitId={unit._id}
                      sectionIndex={sectionIndex}
                    />
                  </SectionHeader>
                }
                key={i}
                id={_id}
                sectionName={name}
                content={content}
                sectionIndex={sectionIndex}
                displayQuestions={i !== 0}
                questions={questions}
                onDeleteSection={this.handleDeleteSection(sectionIndex)}
                canDelete={!subsections || subsections.length === 0}
              />
            ),
          )}
        </StyledAccordion>
        <ActionContainer>
          <Button raised primary onClick={this.handleAddSection}>
            Add Section
          </Button>
        </ActionContainer>
        <MappingFooter unit={unit} sectionIndex={sectionIndex} />
        <Route
          exact
          path={`/units/:unitId/learner-guide/${sectionIndex.join(
            '.',
          )}/:sectionIndex/mapping`}
          component={SectionMapping}
        />
        <Route
          path={`/units/:unitId/learner-guide/${sectionIndex.join(
            '.',
          )}/:sectionIndex/question/:questionIndex?`}
          component={QuestionContent}
        />
        <Route
          path={`/units/:unitId/learner-guide/${sectionIndex.join(
            '.',
          )}/:sectionIndex/question/:questionIndex/mapping`}
          component={QuestionMapping}
        />
      </FullAreaContent>
    )
  }
}

export default withState(injectState(Content))



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/LearnerGuide/SectionContent/index.js